import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Clock, XCircle, Loader2, ExternalLink, Trash2, Save, Copy, Mail, Phone } from 'lucide-react';
import { adminListBookings, adminUpdateBooking, adminDeleteBooking } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';

const STATUS_OPTIONS = [
  'pending_payment',
  'paid',
  'confirmed',
  'completed',
  'cancelled',
  'refunded',
];

function statusBadge(s) {
  const map = {
    pending_payment: { color: 'bg-[rgba(245,185,66,0.16)] text-[hsl(43_92%_56%)] border-[rgba(245,185,66,0.42)]', Icon: Clock },
    paid: { color: 'bg-[rgba(108,176,255,0.18)] text-[hsl(212_80%_70%)] border-[rgba(108,176,255,0.42)]', Icon: CheckCircle2 },
    confirmed: { color: 'bg-[rgba(108,176,255,0.18)] text-[hsl(212_80%_70%)] border-[rgba(108,176,255,0.42)]', Icon: CheckCircle2 },
    completed: { color: 'bg-[rgba(70,200,120,0.18)] text-[hsl(140_70%_60%)] border-[rgba(70,200,120,0.50)]', Icon: CheckCircle2 },
    cancelled: { color: 'bg-[rgba(225,29,46,0.16)] text-[hsl(355_75%_64%)] border-[rgba(225,29,46,0.42)]', Icon: XCircle },
    refunded: { color: 'bg-[rgba(225,29,46,0.16)] text-[hsl(355_75%_64%)] border-[rgba(225,29,46,0.42)]', Icon: XCircle },
  };
  const def = map[s] || { color: 'bg-white/10 text-white border-white/20', Icon: Clock };
  return def;
}

export default function AdminBookingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListBookings(300);
      setItems(res.items || []);
    } catch (e) {
      toast.error('Failed to load bookings');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = items.reduce((acc, b) => {
    acc.total += 1;
    if (b.status === 'paid') acc.paid += 1;
    if (b.status === 'pending_payment') acc.pending += 1;
    if (b.status === 'completed') acc.completed += 1;
    acc.revenue += b.status === 'paid' || b.status === 'confirmed' || b.status === 'completed' ? Number(b.amount_usd || 0) : 0;
    return acc;
  }, { total: 0, paid: 0, pending: 0, completed: 0, revenue: 0 });

  async function persistUpdate(patch) {
    if (!selected) return;
    setSaving(true);
    try {
      await adminUpdateBooking(selected.id, patch);
      toast.success('Updated');
      await load();
      setSelected((p) => ({ ...p, ...patch }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  async function deleteBooking(id) {
    if (!window.confirm('Delete this booking permanently?')) return;
    try {
      await adminDeleteBooking(id);
      toast.success('Deleted');
      setSelected(null);
      await load();
    } catch (e) {
      toast.error('Delete failed');
    }
  }

  function copy(val) {
    try {
      navigator.clipboard.writeText(val || '');
      toast.success('Copied');
    } catch {}
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display uppercase text-2xl">Bookings · 1-on-1 Calls</h2>
        <p className="text-xs text-muted-foreground mt-1">Manage incoming consultation bookings. Statuses update via NOWPayments IPN webhook + manual admin actions.</p>
      </div>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: 'Total', value: summary.total },
          { label: 'Pending', value: summary.pending },
          { label: 'Paid', value: summary.paid },
          { label: 'Completed', value: summary.completed },
          { label: 'Revenue (paid+)', value: `$${summary.revenue.toFixed(0)}` },
        ].map((k) => (
          <Card key={k.label} className="card-surface" data-testid={`bookings-kpi-${k.label.toLowerCase()}`}>
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k.label}</div>
              <div className="font-display text-2xl text-[hsl(43_92%_56%)] mt-1">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card className="card-surface">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">All Bookings ({items.length})</CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px] uppercase tracking-wider">Created</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Plan</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Amount</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No bookings yet.</TableCell></TableRow>
              )}
              {items.map((b) => {
                const bd = statusBadge(b.status);
                return (
                  <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {(b.created_at || '').slice(0, 19).replace('T', ' ')}
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold">{b.name}</div>
                      <div className="text-xs text-muted-foreground">{b.email}</div>
                    </TableCell>
                    <TableCell className="text-sm uppercase font-mono">{b.plan}</TableCell>
                    <TableCell className="text-[hsl(43_92%_56%)] font-semibold">${b.amount_usd}</TableCell>
                    <TableCell>
                      <Badge className={'border ' + bd.color + ' font-semibold uppercase text-[10px] tracking-[0.14em] gap-1'}>
                        <bd.Icon className="h-3 w-3" />
                        {b.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => setSelected(b)} data-testid={`booking-manage-${b.id}`}>
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Selected booking detail panel */}
      {selected && (
        <Card className="card-surface" data-testid="booking-detail">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Booking · {selected.name}</CardTitle>
            <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Info label="ID" value={selected.id} copyable onCopy={copy} />
              <Info label="Email" value={selected.email} Icon={Mail} copyable onCopy={copy} />
              <Info label="Contact" value={selected.contact} Icon={Phone} copyable onCopy={copy} />
              <Info label="Preferred date/time" value={selected.preferred_datetime} />
              <Info label="Country" value={`${selected.country_code || '—'} · ${selected.country_name || ''}`} />
              <Info label="Plan" value={`${selected.plan} · $${selected.amount_usd}`} />
              <Info label="Pay currency" value={selected.pay_currency} />
              <Info label="NOWPayments ID" value={selected.nowpayments_payment_id || '—'} copyable onCopy={copy} />
            </div>

            {selected.nowpayments_invoice_url && (
              <a
                href={selected.nowpayments_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[hsl(212_80%_70%)] hover:underline"
                data-testid="booking-invoice-link"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open NOWPayments invoice
              </a>
            )}

            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Topic</label>
              <div className="text-sm bg-white/[0.03] border border-white/10 rounded-md p-3 mt-1 whitespace-pre-line">
                {selected.topic}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Status</label>
                <select
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-md h-9 px-2 text-sm"
                  value={selected.status || ''}
                  onChange={(e) => persistUpdate({ status: e.target.value })}
                  data-testid="booking-status-select"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  {selected.status && !STATUS_OPTIONS.includes(selected.status) && (
                    <option value={selected.status}>{selected.status}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Meeting link (Zoom / Google Meet)</label>
                <Input
                  value={selected.meeting_link || ''}
                  onChange={(e) => setSelected((p) => ({ ...p, meeting_link: e.target.value }))}
                  placeholder="https://meet.google.com/..."
                  data-testid="booking-meeting-link"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Admin notes</label>
              <Textarea
                rows={3}
                value={selected.admin_notes || ''}
                onChange={(e) => setSelected((p) => ({ ...p, admin_notes: e.target.value }))}
                placeholder="Internal notes about this booking"
                data-testid="booking-admin-notes"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => persistUpdate({ meeting_link: selected.meeting_link || '', admin_notes: selected.admin_notes || '' })}
                disabled={saving}
                className="cta-sheen border-0"
                data-testid="booking-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save link & notes</>}
              </Button>
              <Button variant="outline" onClick={() => deleteBooking(selected.id)} data-testid="booking-delete">
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Info({ label, value, Icon, copyable, onCopy }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="text-sm mt-1 inline-flex items-center gap-1.5 break-all">
        {Icon && <Icon className="h-3 w-3 text-[hsl(43_92%_56%)] shrink-0" />}
        <span>{value || '—'}</span>
        {copyable && value && (
          <button onClick={() => onCopy(value)} className="text-muted-foreground hover:text-white">
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
